/**
 * Top-level router (createBrowserRouter, data-router mode).
 */
import { Navigate, createBrowserRouter, type RouteObject } from 'react-router-dom';
import { RootLayout } from '@/routes/RootLayout';
import { AuthGate } from '@/auth/AuthGate';
import { AppLayout } from '@/components/AppLayout';
import { AuthReturn } from '@/routes/AuthReturn';
import { SignedOut } from '@/routes/SignedOut';
import { AuthError } from '@/routes/AuthError';
import { NotFound } from '@/routes/NotFound';
import { Forbidden } from '@/routes/Forbidden';
import { RouteError } from '@/routes/RouteError';
import { Home, homeLoader } from '@/routes/Home';
import { TenantsList, tenantsListLoader } from '@/features/tenants/TenantsList';
import { TenantWorkspace, tenantWorkspaceLoader } from '@/features/tenants/TenantWorkspace';
import { TenantOverview } from '@/features/tenants/TenantOverview';
import { TenantHostnames } from '@/features/tenants/TenantHostnames';
import { TenantBranding } from '@/features/tenants/TenantBranding';
import { TenantMembers } from '@/features/tenants/TenantMembers';
import { TenantInvitations } from '@/features/tenants/TenantInvitations';
import { TenantEmailTemplates } from '@/features/tenants/TenantEmailTemplates';
import { TenantIntegrationSettings } from '@/features/tenants/TenantIntegrationSettings';
import { TenantSettings } from '@/features/tenants/TenantSettings';
import { TenantOidcSso } from '@/features/tenants/TenantOidcSso';
import { TenantEntraSso } from '@/features/tenants/TenantEntraSso';
import { NewTenantWizard } from '@/features/workflows/NewTenantWizard';
import { ReassignProductWizard } from '@/features/workflows/ReassignProductWizard';
import { BrandsList, brandsListLoader } from '@/features/brands/BrandsList';
import { BrandEditor, brandEditorLoader } from '@/features/brands/BrandEditor';
import { UsersList, usersListLoader } from '@/features/users/UsersList';
import { UserDetail, userDetailLoader } from '@/features/users/UserDetail';
import { ProductsList, productsListLoader } from '@/features/reference/ProductsList';
import {
  ProductAdminsTab,
  ProductAgentsTab,
  ProductAppsIndexRedirect,
  ProductAppsLayout,
  ProductAppsTab,
  ProductDetail,
  ProductGhlMarketplaceTab,
  ProductInternalKeysTab,
  ProductLimitDefinitionsTab,
  ProductOidcClientsTab,
  ProductPlansTab,
  ProductResellerAdminTab,
  productDetailLoader,
} from '@/features/reference/ProductDetail';
import { ProductEditor, productEditorLoader } from '@/features/reference/ProductEditor';
import { OidcClientsList, oidcClientsListLoader } from '@/features/reference/OidcClientsList';
import { OidcClientDetail, oidcClientDetailLoader } from '@/features/reference/OidcClientDetail';
import { OidcClientCreate } from '@/features/reference/OidcClientCreate';
import { OperatorsPanel, operatorsListLoader } from '@/features/reference/OperatorsPanel';
import { PlatformOperations } from '@/features/operations/PlatformOperations';
import { BrandLoginPreview } from '@/features/brands/BrandLoginPreview';

const routes: RouteObject[] = [
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <RouteError />,
    children: [
      // Public auth routes (no AuthGate).
      { path: 'auth/return', element: <AuthReturn /> },
      { path: 'auth/signed-out', element: <SignedOut /> },
      { path: 'auth/error', element: <AuthError /> },
      // Brand login preview (no AuthGate, reads from localStorage).
      { path: 'brands/preview', element: <BrandLoginPreview /> },

      // Everything else is gated.
      {
        element: <AuthGate />,
        children: [
          {
            element: <AppLayout />,
            children: [
              { index: true, element: <Home />, loader: homeLoader },
              { path: 'forbidden', element: <Forbidden /> },

              { path: 'tenants', element: <TenantsList />, loader: tenantsListLoader },
              {
                path: 'tenants/:id',
                id: 'tenant-workspace',
                element: <TenantWorkspace />,
                loader: tenantWorkspaceLoader,
                children: [
                  { index: true, element: <Navigate to="overview" replace /> },
                  { path: 'overview', element: <TenantOverview /> },
                  { path: 'whitelabel', element: <TenantHostnames /> },
                  { path: 'branding', element: <TenantBranding /> },
                  { path: 'members', element: <TenantMembers /> },
                  { path: 'invitations', element: <TenantInvitations /> },
                  { path: 'email-templates', element: <TenantEmailTemplates /> },
                  { path: 'integration-settings', element: <TenantIntegrationSettings /> },
                  { path: 'settings', element: <TenantSettings /> },
                  { path: 'oidc', element: <TenantOidcSso /> },
                  { path: 'microsoft-sso', element: <TenantEntraSso /> },
                ],
              },

              { path: 'workflows/new-tenant/:tenantId?', element: <NewTenantWizard /> },
              {
                path: 'workflows/tenants/:id/reassign-product',
                element: <ReassignProductWizard />,
              },

              { path: 'brands', element: <BrandsList />, loader: brandsListLoader },
              { path: 'brands/new', element: <BrandEditor />, loader: brandEditorLoader },
              { path: 'brands/:id', element: <BrandEditor />, loader: brandEditorLoader },

              { path: 'users', element: <UsersList />, loader: usersListLoader },
              { path: 'users/:id', element: <UserDetail />, loader: userDetailLoader },

              {
                path: 'reference/products',
                element: <ProductsList />,
                loader: productsListLoader,
              },
              {
                path: 'reference/products/new',
                element: <ProductEditor />,
                loader: productEditorLoader,
              },
              {
                path: 'reference/products/:id',
                id: 'product-detail',
                element: <ProductDetail />,
                loader: productDetailLoader,
                children: [
                  { index: true, element: <Navigate to="apps" replace /> },
                  {
                    path: 'apps',
                    element: <ProductAppsLayout />,
                    children: [
                      { index: true, element: <ProductAppsIndexRedirect /> },
                      { path: 'public', element: <ProductAppsTab appRole="PUBLIC" /> },
                      { path: 'tenant-admin', element: <ProductAppsTab appRole="TENANT_ADMIN" /> },
                      { path: 'api', element: <ProductAppsTab appRole="API" /> },
                      { path: 'affiliate', element: <ProductAppsTab appRole="AFFILIATE" /> },
                      {
                        path: 'self-contained',
                        element: <ProductAppsTab appRole="SELF_CONTAINED" />,
                      },
                      { path: 'reseller-admin', element: <ProductResellerAdminTab /> },
                      { path: 'product-admin', element: <ProductAdminsTab /> },
                    ],
                  },
                  {
                    path: 'product-admins',
                    element: <Navigate to="../apps/product-admin" replace />,
                  },
                  { path: 'internal-keys', element: <ProductInternalKeysTab /> },
                  { path: 'limits', element: <ProductLimitDefinitionsTab /> },
                  { path: 'plans', element: <ProductPlansTab /> },
                  { path: 'oidc', element: <ProductOidcClientsTab /> },
                  { path: 'agents', element: <ProductAgentsTab /> },
                  { path: 'ghl-marketplace', element: <ProductGhlMarketplaceTab /> },
                ],
              },
              {
                path: 'reference/products/:id/edit',
                element: <ProductEditor />,
                loader: productEditorLoader,
              },
              {
                path: 'reference/oidc-clients',
                element: <OidcClientsList />,
                loader: oidcClientsListLoader,
              },
              { path: 'reference/oidc-clients/new', element: <OidcClientCreate /> },
              {
                path: 'reference/oidc-clients/:id',
                element: <OidcClientDetail />,
                loader: oidcClientDetailLoader,
              },
              {
                path: 'reference/operators',
                element: <OperatorsPanel />,
                loader: operatorsListLoader,
              },
              { path: 'operations', element: <PlatformOperations /> },

              { path: '*', element: <NotFound /> },
            ],
          },
        ],
      },
    ],
  },
];

export const router = createBrowserRouter(routes);
